// Authorization for Unit Export V1 — deliberately built from the SAME
// primitives (isAdmin/hasCouncilFullRead, profile.districtId) that
// useVisibleUnits()/useVisibleUnitLeaders()/useVisibleSyt()/
// useUnitConnections() (src/hooks/useUnits.ts) already use to scope their
// Firestore queries, and that firestore.rules' inMyDistrict()-shaped rule
// blocks already enforce server-side for every unit-related collection
// (units/unitLeaders/unitSyt/unitConnections/unitAdvancementSnapshots/
// unitRecharterSnapshots/unitKey3Snapshots/unitSytSnapshots all share this
// EXACT SAME read shape — verified directly against firestore.rules before
// writing this file). This is not a parallel export-only permission system
// — it is a named, testable restatement of the one rule Compass's unit
// data already has.
//
// A unit reachable through the live UI's own navigation (opening a drawer)
// was ALREADY filtered through this exact boundary before it ever reached
// the component — canExportUnit() below is enforced again here as
// defense-in-depth, not because the live UI can actually present an
// unauthorized unit today.

import { isAdmin, hasCouncilFullRead } from '../visibility.ts';
import type { Unit, UserProfile } from '../../types.ts';

/** The one authorization boundary Compass's unit-related data currently
 *  has: admin, council-wide, or the caller's own district. */
export function canExportUnit(profile: UserProfile | null, unit: Pick<Unit, 'districtId'>): boolean {
  if (!profile) return false;
  if (isAdmin(profile) || hasCouncilFullRead(profile)) return true;
  return !!profile.districtId && profile.districtId === unit.districtId;
}

/** Whether leader contact fields (email/phone) may appear in the export.
 *
 * Compass does NOT currently have a permission tier narrower than plain
 * unit visibility for contact fields specifically — verified directly
 * against src/views/UnitHealth.tsx: every role that can see a unit's
 * Leaders tab/drawer at all (member, chair, districtView, keyThree,
 * council-wide, admin) already sees that leader's email/phone
 * unconditionally in the live app today. So this function is not
 * currently a narrower check than canExportUnit() — it exists as its own
 * named seam so that IF Compass later adds a real finer-grained contact
 * permission, only this one function needs to change (and the export
 * would then be MORE restrictive than the live UI is today, which is
 * backwards — that live-UI gap would need closing first, not papered
 * over here). See the Unit Export V1 completion report for this finding
 * spelled out in full. */
export function canExportLeaderContactFields(profile: UserProfile | null, unit: Pick<Unit, 'districtId'>): boolean {
  return canExportUnit(profile, unit);
}
